import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UUID } from "./model/uuid";
import { CommunicationProps } from "./props";
import Section from "./structure/section";
import {
	Entity,
	EntityMention,
	EntityMentionSet,
	EntitySet,
} from "./model/entities";
import { v4 as uuidv4 } from "uuid";
import { SKIP_DEACTIVATE, basic_actions_instructions, entity_mention_instructions, entity_refernt_instructions, logoSVG, situation_mention_instructions, situation_refernt_instructions } from "./constants";
import { Alert, Snackbar } from "@mui/material";
import { Communication as CommunicationModel } from "./model/communication";
import { SpanNode } from "./structure/span/span-tree";
import MentionAnnotation from "./annotation/mention";
import ReferentAnnotation from "./annotation/referent";
import { TheoryDependencies } from "./model/metadata";
import {
	SpanTreeContext,
	TokenizationContext,
	MentionModelContext,
	ModalOpenContext,
	MentionAnnotationContext,
} from "./contexts";
import { SectionAnnotationScheme } from "./annotation/scheme";
import {
	SituationSet,
	SituationMention,
	SituationMentionSet,
	Situation,
	Argument,
} from "./model/situations";
import { Situation as SituationIntermediate } from "./structure/intermediates";
import ToolTip, { ToolTipItemProps } from "./UI/ToolTip";
import convertSituationtoSituationIntermediate from "./utils/conversionScripts";
import {
	returnEntityMentionList,
	returnSituationMentionList,
} from "./utils/saveScripts";
import Modal from "./UI/Modal";
import { UserInteractionInfo } from './annotation/user-interaction';
import { constants } from 'buffer';
// import ReactMarkdown from 'react-markdown';

export type MentionModel = "entity" | "situation";
export type ToastType = "success" | "info" | "error" | "warning";
export default function Communication(props: CommunicationProps) {
	const entityMentionList = useRef<EntityMention[]>(
		initializeEntityMentionList()
	);
	const situationMentionList = useRef<SituationMention[]>(
		initializeSituationMentionList()
	);

	const metadata = props.annotationScheme.metadata;

	const [activeStructureIds, setActiveStructureIds] = useState<
		[UUID, UUID, MentionModel][]
	>([]);

	const [activeSituation, setActiveSituation] = useState<UUID[]>([]);
	const [activeTypeIds, setActiveTypeIds] = useState<UUID[]>([]);
	const [deleteSpanCallback, setDeleteSpanCallback] = useState<Function>(
		() => null
	);
	const [entityList, setEntityList] = useState<Entity[]>(
		initializeEntityList()
	);
	const [highlightedMentions, setHighlightedMentions] = useState<Set<string>>(
		new Set<string>()
	);
	const [situationList, setSituationList] = useState<SituationIntermediate[]>(
		initializeSituationList()
	);
	const [spanMap, setSpanMap] = useState<Map<string, string>>(
		new Map<string, string>()
	);
	const [toast, setToast] = useState<boolean>(false);
	const [toastMessage, setToastMessage] = useState<string>("");
	const [toastType, setToastType] = useState<ToastType>("success");
	const [mentionModel, setMentionModel] = useState<"entity" | "situation">(
		initializeMentionModel()
	);
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [spanToTypeMap, setSpanToTypeMap] = useState<Map<string, string>>(
		new Map<string, string>()
	);
	const [situationArgumentMap, setSituationArgumentMap] = useState<
		Map<string, Map<string, Set<string>>>
	>(new Map<string, Map<string, Set<string>>>());

	const spanTreeMap = useContext(SpanTreeContext);

	const theoryDependencies = useRef<TheoryDependencies>(
		theoryDependencyCompute()
	);

	const [infoModalShow, setInfoModalShow] = useState<boolean>(
		props.annotationScheme.instructions !== undefined
	);
	const [instructionContent, setInstructionContent] = useState<JSX.Element>(_getInstructionContent());

	const [metaKeyClicked, setMetaKeyClicked] = useState<boolean>(false);
	const [shiftKeyClicked, setShiftKeyClicked] = useState<boolean>(false);
	const [hoveredSpan, setHoveredSpan] = useState<string>("");

	const [userInteractions, setUserInteractions] = useState<UserInteractionInfo[]>([]);

	// Reinitialize all states for next MTurk hit.
	useEffect(() => {
		entityMentionList.current = initializeEntityMentionList();
		situationMentionList.current = initializeSituationMentionList();
		setEntityList(initializeEntityList());
		setSituationList(initializeSituationList());
		setMentionModel(initializeMentionModel());
		theoryDependencies.current = theoryDependencyCompute();
		setInfoModalShow(props.annotationScheme.instructions !== undefined);
		// setInstructionContent(_getInstructionContent());
		setInstructionContent(__instructionContentSet(props.annotationScheme.subkind, props.annotationScheme.kind));
		setUserInteractions([]);
	}, [props.communication, props.annotationScheme])

	useEffect(() => {
		// Deactivate all structures on Escape key
		const deactivator = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				if (activeStructureIds.length) {
					deactivateStructureIds();
				}
			}
		};
		document.addEventListener("keydown", deactivator);
		return () => document.removeEventListener("keydown", deactivator);
	}, []);

	// TODO: Revisit and rethink.
	useEffect(() => {
		// deactivate all structures if any element with className not included in
		// CONSTANTS.skipStructureDeactivation is clicked
		const deactivator = (e: MouseEvent) => {
			if (e.target && !(e.metaKey || e.ctrlKey)) {
				const target = e.target as Element;
				if (!SKIP_DEACTIVATE.has(target.className)) {
					if (activeStructureIds.length) {
						deactivateStructureIds();
					} else {
						activateTypeIds();
					}
				}
			}
		};
		document.addEventListener("click", deactivator);
		return () => document.removeEventListener("click", deactivator);
	}, [activeStructureIds]);

	// TODO: Revisit and rethink
	useEffect(() => {
		// Callback for deleting spans on backspace or delete key events
		const callback = (e: KeyboardEvent) => {
			if (
				(e.key === "Backspace" || e.key === "Delete") &&
				activeStructureIds[0]?.length
			) {
				if (
					!activeSituation.length ||
					(activeSituation.length &&
						activeSituation[0].uuidString !==
							activeStructureIds[0][0].uuidString)
				) {
					if (activeStructureIds[0][2] !== mentionModel) {
						displayToast(
							`Cannot delete ${activeStructureIds[0][2].toUpperCase()} in ${mentionModel.toUpperCase()} mode`,
							"error"
						);
					} else {
						const objectType =
							activeStructureIds[0][2] === "entity"
								? "entityMention"
								: "situationMention";
						addToUserInteractions(
							"delete",
							{
								type: objectType,
								text: activeStructureIds[0][0].uuidString,
							},
							undefined,
							undefined,
							undefined
						);
						deleteSpanCallback();
						deleteFromSpanMap(activeStructureIds[0][0]);
						deactivateStructureIds();
					}
				} else {
					displayToast("Cannot delete active situation", "error");
				}
			}
		};
		document.addEventListener("keydown", callback);
		return () => {
			document.removeEventListener("keydown", callback);
		};
	}, [deleteSpanCallback, activeStructureIds, mentionModel]);

	useEffect(() => {
		const callback = (e: KeyboardEvent) => {
			if (e.metaKey || e.ctrlKey) {
				setMetaKeyClicked(true);
			}

			if (e.shiftKey) {
				setShiftKeyClicked(true);
			}
		};

		document.addEventListener("keydown", callback);
		return () => {
			document.removeEventListener("keydown", callback);
		};
	});

	useEffect(() => {
		const callback = (e: KeyboardEvent) => {
			if (e.key === "Meta" || e.key === "Control") {
				setMetaKeyClicked(false);
			}
			if (e.key === "Shift") {
				setShiftKeyClicked(false);
			}
		};

		document.addEventListener("keyup", callback);
		return () => {
			document.removeEventListener("keyup", callback);
		};
	});

	useEffect(() => {
		if (props.annotationScheme.kind === "referent") {
			if (mentionModel === "entity") {
				const activeEntity = entityList.find(
					(entity) => entity.uuid === activeTypeIds[0]
				);
				let mentionSet: Set<string>;
				if (activeEntity !== undefined) {
					mentionSet = new Set<string>(
						activeEntity.mentionIdList.map(
							(uuid) => uuid.uuidString
						)
					);
				} else {
					mentionSet = new Set<string>();
				}
				setHighlightedMentions(mentionSet);
			} else {
				const activeSituation = situationList.find(
					(entity) => entity.uuid === activeTypeIds[0]
				);
				let mentionSet: Set<string>;
				if (activeSituation !== undefined) {
					mentionSet = new Set<string>(
						activeSituation.mentionIdList?.map(
							(uuid) => uuid.uuidString
						)
					);
				} else {
					mentionSet = new Set<string>();
				}
				setHighlightedMentions(mentionSet);
			}
		}
	}, [activeTypeIds, entityList, situationList, mentionModel]);

	// Initializes mentionModel everytime communication 
	// or annotationScheme is changed
	useEffect(() => {
		setMentionModel(initializeMentionModel());
	}, [props.communication, props.annotationScheme])

	/**
	 * Displays toast message on the UI for a set time
	 */
	const displayToast = useCallback(
		(message: string, toastType: ToastType) => {
			setToast(true);
			setToastMessage(message);
			setToastType(toastType);
		},
		[]
	);

	const MentionAnnotationContextValue = {
		spanToTypeMap,
		setSpanToTypeMap,
		situationArgumentMap,
		setSituationArgumentMap,
		displayToast,
		addToUserInteractions,
	};

	/**
	 * Converts stringified html instructions provided in config file to JSX Element
	 */
	function _getInstructionContent(): JSX.Element {
		const htmlString = props.annotationScheme.instructions;
		return (
			<ErrorBoundary fallback={<div>There is an error parsing instructions for this annotation.</div>}>
				<div className={"empty-info"} dangerouslySetInnerHTML={{__html:htmlString || ""}}/>
			</ErrorBoundary>
		);
	}

	/**
	 * helper method to add user activity
	 *
	 * @param message
	 * string stating how the user has interacted with the UI
	 */
	function addToUserInteractions(
		action: "create" | "delete" | "set" | "add",
		object: {
			type: "entityMention" | "situationMention" | "entity" | "situation";
			text: string;
		},
		attribute?: {
			type: string;
			value: string;
		},
		type?: string,
		role?: string,
		referent?: string,
		referredSituationMention?: string
	): void { 
		const interactions = [...userInteractions]
		interactions.push({
			action: action,
			object: object,
			referentAttribute: attribute,
			type: type,
			role: role,
			referent: referent,
			referredSituationMention: referredSituationMention,
			timestamp: Math.trunc(Date.now() / 1000),
		});
		setUserInteractions(interactions);
	}
	/**
	 * Initializes the mention model on the basis of task
	 *
	 * @returns
	 * mention model i.e. entity | situation based on the task at hand
	 */
	function initializeMentionModel(): "entity" | "situation" {
		if (
			props.annotationScheme.kind === "mention" &&
			props.annotationScheme.subkind === "situation"
		)
			return "situation";
		return "entity";
	}

	/**
	 * @returns
	 * Theory Dependencies based on annotation scheme
	 */
	function theoryDependencyCompute(): TheoryDependencies {
		const theoryDependencies: TheoryDependencies = {};

		const entityMentionSet = props.entityMentionSetList?.find(
			(set: EntityMentionSet) =>
				set.metadata?.tool === props.annotationScheme.metadata?.tool &&
				set.metadata?.timestamp ===
					props.annotationScheme.metadata?.timestamp
		);

		// TODO: Add Situation Theory Dependency
		if (
			props.annotationScheme.kind === "referent" &&
			entityMentionSet !== undefined
		) {
			if (entityMentionSet !== undefined) {
				if (theoryDependencies.entityMentionSetTheoryList === undefined)
					theoryDependencies.entityMentionSetTheoryList = [
						entityMentionSet.uuid,
					];
				else
					theoryDependencies.entityMentionSetTheoryList.push(
						entityMentionSet.uuid
					);
			}
		}

		return theoryDependencies;
	}

	/**
	 *
	 * @returns
	 * The EntityList from the communication object
	 */
	function initializeEntityList(): Entity[] {
		let entitySet: EntitySet | undefined = props.entitySetList?.find(
			(elem: EntitySet) =>
				elem.metadata?.tool === props.annotationScheme.metadata?.tool &&
				elem.metadata?.timestamp ===
					props.annotationScheme.metadata?.timestamp
		);
		if (entitySet === undefined) {
			return [];
		}
		return entitySet.entityList;
	}

	/**
	 *
	 * @returns
	 * The SituationList from the communication object
	 */
	function initializeSituationList(): SituationIntermediate[] {
		let situationSet: SituationSet | undefined =
			props.situationSetList?.find(
				(elem: SituationSet) =>
					elem.metadata?.tool ===
						props.annotationScheme.metadata?.tool &&
					elem.metadata?.timestamp ===
						props.annotationScheme.metadata?.timestamp
			);
		if (situationSet === undefined) {
			return [];
		}

		const situationList: SituationIntermediate[] = [];

		situationSet?.situationList?.forEach((situation, idx) => {
			situationList.push(
				convertSituationtoSituationIntermediate(situation)
			);
		});

		return situationList;
	}

	/**
	 *
	 * @returns
	 * The EntityMentionSetList from the communication object
	 */
	// TODO: Rename this method,
	function initializeEntityMentionList(): EntityMention[] {
		let entityMentionSet: EntityMentionSet | undefined =
			props.entityMentionSetList?.find(
				(elem: EntityMentionSet) =>
					elem.metadata?.tool ===
						props.annotationScheme.metadata?.tool &&
					elem.metadata?.timestamp ===
						props.annotationScheme.metadata?.timestamp
			);
		if (entityMentionSet === undefined) {
			return [];
		}
		return entityMentionSet.mentionList;
	}

	/**
	 *
	 * @returns
	 * The EntityMentionSetList from the communication object
	 */
	// TODO: Rename this method,
	function initializeSituationMentionList(): SituationMention[] {
		let situationMentionSet: SituationMentionSet | undefined =
			props.situationMentionSetList?.find(
				(elem: SituationMentionSet) =>
					elem.metadata?.tool ===
						props.annotationScheme.metadata?.tool &&
					elem.metadata?.timestamp ===
						props.annotationScheme.metadata?.timestamp
			);
		if (situationMentionSet === undefined) {
			return [];
		}
		return situationMentionSet.mentionList || [];
	}

	/**
	 * Function to select spans in content.
	 *
	 * @param uuids
	 * UUID of spans to be selected.
	 * @param tokenizationUUIDs
	 * UUID of the tokenization that contains the span to activate
	 * @param deactivateOthers
	 * Boolean to specify if previously selected spans are to be deactivated.
	 * TODO: either implement deactivateOthers or remove it
	 * @returns
	 * list of selected UUIDs.
	 */
	const activateStructureIds = useCallback(
		(
			uuids: UUID[],
			tokenizationUUIDs: UUID[],
			mentionModel: MentionModel
		) => {
			// setActiveStructureIds(uuids);
			setActiveStructureIds([
				[uuids[0], tokenizationUUIDs[0], mentionModel],
			]);
			// setActiveTokenizationIds(tokenizationUUIDs);
			return uuids;
		},
		[]
	);

	/**
	 * Function to activate Situation;
	 * (Only used in situation-mention task)
	 *
	 * @param uuids
	 * UUID of spans to be selected.
	 * @param tokenizationUUIDs
	 * UUID of the tokenization that contains the span to activate
	 * @param deactivateOthers
	 * Boolean to specify if previously selected spans are to be deactivated.
	 * @returns
	 * list of selected UUIDs.
	 */
	const activateSituationId = useCallback((uuids: UUID[]) => {
		setActiveSituation(uuids);
		setMentionModel("entity");
		return uuids;
	}, []);

	/**
	 * Function to deactivate UUIDs.
	 *
	 * @param uuids
	 * List of UUIDs to be deactivated.
	 * @returns
	 * List of active UUIDs.
	 */

	// TODO: this function isn't making use of the parameter, remove from all definitions
	const deactivateStructureIds = useCallback(() => {
		setActiveStructureIds([]);
		// setActiveTokenizationIds([]);
		return [];
	}, [activeStructureIds]);

	/**
	 * Function to deactivate Situation.
	 *
	 * @returns
	 * List of active UUIDs.
	 */
	const deactivateSituation = useCallback(() => {
		setActiveSituation([]);
		setMentionModel("situation");
		return [];
	}, [activeSituation]);

	/**
	 *  Activates the typeIds passed or sets the activeTypeIds to an empty array
	 *
	 * @param uuid
	 * The UUID of the type to be activated
	 */
	const activateTypeIds = useCallback((uuid?: UUID): void => {
		const activeTypeIds = uuid === undefined ? [] : [uuid];
		setActiveTypeIds(activeTypeIds);
	}, []);

	/**
	 * Adds or updates an entry to the SpanMap
	 *
	 * The SpanMap is a Map object that maps the key of a span (its UUID object)
	 * to the text represented by the span (by concatenating all tokens in the span).
	 *
	 * @param span
	 * The UUID object of the span that is being added to the SpanMap
	 * @param val
	 * The string of all tokens contained within the span
	 */
	const addToSpanMap = useCallback((span: UUID, val: string): void => {
		setSpanMap((spanMap) => {
			const newState = new Map(spanMap);
			newState.set(span.uuidString, val);
			return newState;
		});
	}, []);

	/**
	 * Removes a span from the spanMap
	 *
	 * @param span
	 * The UUID object of the span being removed from the SpanMap
	 */
	const deleteFromSpanMap = useCallback(
		(span: UUID): void => {
			setSpanMap((spanMap) => {
				const newSpanMap = new Map(spanMap);
				newSpanMap.delete(span.uuidString);
				if (spanToTypeMap.has(span.uuidString)) {
					const toastMessage = `'${spanMap.get(
						span.uuidString
					)}' deleted from '${spanToTypeMap.get(span.uuidString)}'`;
					displayToast(toastMessage, "success");
				}
				return newSpanMap;
			});
		},
		[spanToTypeMap]
	);

	/**
	 * Creates a new entity and adds the currently active span to it
	 *
	 * @param entity
	 * The entity object to be added to the entityList
	 */
	const createEntity = (entity: Entity): void => {
		setEntityList((entityList) => {
			return [...entityList, entity];
		});
	};
	/**
	 * Removes an entity from the entityList.
	 *
	 * @param uuid
	 * The UUID of the entity that is to be removed from the entityList
	 */
	const deleteEntity = (uuid: UUID): void => {
		setEntityList((entityList) => {
			const newSet = entityList.filter(
				(element) => element.uuid.uuidString !== uuid.uuidString
			);
			const entity = entityList.filter(
				(element) => element.uuid.uuidString === uuid.uuidString
			);
			setActiveTypeIds((activeTypeIds) => {
				if (
					activeTypeIds.length &&
					activeTypeIds[0].uuidString === uuid.uuidString
				) {
					return [];
				}
				return activeTypeIds;
			});
			return newSet;
		});
	};

	/**
	 * Function to add mention to an exiting entity; alerts if the entity is already present,
	 * adds otherwise
	 *
	 * @param entity
	 * The Entity from which mention is to be removed
	 * @param mentions
	 * A list of mention UUIDs which are to be removed from the entity
	 */
	const addMentionToEntity = (entity: Entity, mentions: UUID[]): void => {
		if (mentions.length > 0) {
			let filtered = entity.mentionIdList.filter((elem) =>
				mentions.some((m) => elem.uuidString === m.uuidString)
			);

			if (filtered.length !== 0) {
				const message =
					spanMap.get(activeStructureIds[0][0].uuidString) +
					" present in " +
					entity.canonicalName;
				displayToast(message, "info");
			} else {
				const newList = [...entityList];
				newList.forEach((e) => {
					if (e.uuid.uuidString === entity.uuid.uuidString) {
						e.mentionIdList.push(...mentions);
					}
				});
				const message =
					spanMap.get(activeStructureIds[0][0].uuidString) +
					" added to " +
					entity.canonicalName;

				addToUserInteractions(
					"add",
					{
						type: "entityMention",
						text: spanMap.get(mentions[0].uuidString) || "",
					},
					undefined,
					undefined,
					undefined,
					entity.canonicalName
				);
				setEntityList(newList);
				displayToast(message, "success");
			}
		}
	};

	/**
	 * Removes a Mention(span) from an Entity if it exists in its mentionIdList
	 * @param entity
	 * The Entity from which mention is to be removed
	 * @param mentions
	 * A list of mention UUIDs which are to be removed from the entity
	 */
	const removeMentionFromEntity = (entity: Entity, mention: UUID): void => {
		const index = entity.mentionIdList.findIndex(
			(elem) => elem.uuidString === mention.uuidString
		);

		if (index !== -1) {
			// NOTE: Why does modifying state trigger re-renders in children?
			entity.mentionIdList.splice(index, 1); //delete mention at the index
		}

		const newList = [...entityList];
		newList[
			newList.findIndex(
				(en) => en.uuid.uuidString === entity.uuid.uuidString
			)
		] = entity; // TODO: Direct state mutation, fix later
		const message =
			spanMap.get(mention.uuidString) +
			" removed from " +
			entity.canonicalName;
		addToUserInteractions(
			"delete",
			{
				type: "entityMention",
				text: spanMap.get(mention.uuidString) || "",
			},
			undefined,
			undefined,
			undefined,
			entity.canonicalName
		);
		displayToast(message, "success");
		setEntityList(newList);
	};

	/**
	 * Sets any property of an entity.
	 *
	 * @param attribute
	 * The string name of the property being set.
	 * @param value
	 * The value to set the attribute to.
	 * @param uuid
	 * The uuidString that identifies an entity object.
	 */
	const mutateEntity = useCallback(
		(
			attribute: "canonicalName" | "type",
			value: string,
			uuid: string
		): void => {
			const newEntityList = [...entityList];
			newEntityList.forEach((entity) => {
				if (entity.uuid.uuidString === uuid) {
					entity[attribute] = value;
				}
			});
			setEntityList(newEntityList);
		},
		[entityList]
	);

	/**
	 * Creates a new situation and adds the currently active span to it
	 *
	 * @param situation
	 * The situation object to be added to the situationList
	 */
	const createSituation = useCallback(
		(situation: SituationIntermediate): void => {
			setSituationList((situationList) => {
				return [...situationList, situation];
			});
			const message =
				(activeStructureIds.length
					? spanMap.get(activeStructureIds[0][0].uuidString) +
					  " added to "
					: "Created ") + situation.canonicalName;
			displayToast(message, "success");
		},
		[spanMap, activeStructureIds]
	);

	/**
	 * Removes an situation from the situationList.
	 *
	 * @param uuid
	 * The UUID of the situation that is to be removed from the situationList
	 */
	const deleteSituation = useCallback((uuid: UUID): void => {
		setSituationList((situationList) => {
			const newSet = situationList.filter(
				(element) => element.uuid.uuidString !== uuid.uuidString
			);

			const situation = situationList.filter(
				(element) => element.uuid.uuidString === uuid.uuidString
			);
			setActiveTypeIds((activeTypeIds) => {
				if (
					activeTypeIds.length &&
					activeTypeIds[0].uuidString === uuid.uuidString
				) {
					return [];
				}
				return activeTypeIds;
			});
			return newSet;
		});
	}, []);

	/**
	 * Function to add mention to an exiting situation; alerts if the situation is already present,
	 * adds otherwise
	 *
	 * @param situation
	 * The Situation from which mention is to be removed
	 * @param mentions
	 * A list of mention UUIDs which are to be removed from the situation
	 */
	const addMentionToSituation = (
		situation: SituationIntermediate,
		mentions: UUID[]
	): void => {
		if (mentions.length > 0) {
			let filtered =
				situation.mentionIdList?.filter((elem) =>
					mentions.some((m) => elem.uuidString === m.uuidString)
				) || [];

			if (filtered.length !== 0) {
				// setToast(true);
				const message =
					spanMap.get(activeStructureIds[0][0].uuidString) +
					" present in " +
					situation.canonicalName;
				// setToastMessage(message);
				displayToast(message, "info");
			} else {
				const newList = [...situationList];
				newList.forEach((s) => {
					if (s.uuid.uuidString === situation.uuid.uuidString) {
						s.mentionIdList?.push(...mentions);
					}
				});
				const message =
					spanMap.get(activeStructureIds[0][0].uuidString) +
					" added to " +
					situation.canonicalName;
				addToUserInteractions(
					"add",
					{
						type: "situationMention",
						text: spanMap.get(mentions[0].uuidString) || "",
					},
					undefined,
					undefined,
					undefined,
					situation.canonicalName
				);
				displayToast(message, "success");
				setSituationList(newList);
			}
		}
	};

	/**
	 * Removes a Mention(span) from an Situation if it exists in its mentionIdList
	 * @param situation
	 * The Situation from which mention is to be removed
	 * @param mentions
	 * A list of mention UUIDs which are to be removed from the sitiation
	 */
	const removeMentionFromSituation = (
		situation: SituationIntermediate,
		mention: UUID
	): void => {
		const index = situation.mentionIdList!.findIndex(
			(elem) => elem.uuidString === mention.uuidString
		);

		if (index !== -1) {
			// NOTE: Why does modifying state trigger re-renders in children?
			situation.mentionIdList?.splice(index, 1); //delete mention at the index
		}
		const newList = [...situationList];
		newList[
			newList.findIndex(
				(en) => en.uuid.uuidString === situation.uuid.uuidString
			)
		] = situation; // TODO: Direct state mutation, fix later
		const message =
			spanMap.get(mention.uuidString) +
			" removed from " +
			situation.canonicalName;
		addToUserInteractions(
			"delete",
			{
				type: "situationMention",
				text: spanMap.get(mention.uuidString) || "",
			},
			undefined,
			undefined,
			undefined,
			situation.canonicalName
		);
		displayToast(message, "success");
		setSituationList(newList);
	};

	/**
	 * Sets any property of an situation.
	 *
	 * @param attribute
	 * The string name of the property being set.
	 * @param value
	 * The value to set the attribute to.
	 * @param uuid
	 * The uuidString that identifies an entity/situation object.
	 */
	const mutateSituation = useCallback(
		(
			attribute: string,
			value: string,
			uuid: string,
			kind?: "entity" | "situation" | "options"
		): void => {
			const newSituationList = [...situationList];
			newSituationList.forEach((situation) => {
				if (situation.uuid.uuidString === uuid) {
					if (
						attribute === "canonicalName" ||
						attribute === "situationType"
					) {
						situation[attribute] = value;
						if (attribute === "situationType") {
							situation.argumentList = [];
						}
					} else {
						if (situation.argumentList === undefined) {
							situation.argumentList = [];
						}

						let flag = false;
						situation.argumentList.forEach((arg, index) => {
							if (arg.role === attribute) {
								flag = true;
								if ("entityId" in arg) arg.entityId!.add(value);
							} else if ("situationId" in arg) {
								arg.situationId!.add(value);
							}
						});

						if (!flag) {
							if (kind === "entity") {
								const entityId = new Set<string>();
								entityId.add(value);
								situation.argumentList.push({
									entityId: entityId,
									role: attribute,
								});
							} else if (kind === "situation") {
								const situationId = new Set<string>();
								situationId.add(value);
								situation.argumentList.push({
									situationId: situationId,
									role: attribute,
								});
							}
						}
					}
				}
			});
			setSituationList(newSituationList);
		},
		[situationList]
	);

	/**
	 * Saves the current state of the annotation to concrete and downloads the JSON file.
	 */
	function saveToConcrete(): CommunicationModel {
		let output = { ...props.communication };

		if (props.annotationScheme.kind === "referent") {
			const entityMentionList: EntityMention[] = [];
			const situationMentionList: SituationMention[] = [];

			const metadata = {
				tool: "FACTSLab",
				timestamp: Math.trunc(Date.now() / 1000), // TODO
				theoryDependencies: theoryDependencies.current,
			};

			const entityMentionMap = new Map<string, EntityMention>();
			const situationMentionMap = new Map<string, SituationMention>();

			spanTreeMap.forEach((spanTree, uuid) => {
				const spans = spanTree.nodes.filter(
					(node) => node.kind === "span"
				) as SpanNode[];
				spans.forEach((span) => {
					const presentInMentionMap = entityMentionMap.has(
						span.span.spanId.uuidString
					);
					const presentInSituationMap = situationMentionMap.has(
						span.span.spanId.uuidString
					);

					if (presentInMentionMap) {
						const updateMention = entityMentionMap.get(
							span.span.spanId.uuidString
						);

						if (updateMention !== undefined) {
							updateMention?.tokens.tokenIndexList.push(
								...span.span.tokenIndexList
							);
							entityMentionMap.set(
								span.span.spanId.uuidString,
								updateMention
							);
						}
					} else if (presentInSituationMap) {
						const updateMention = situationMentionMap.get(
							span.span.spanId.uuidString
						);

						if (updateMention !== undefined) {
							updateMention.tokens?.tokenIndexList.push(
								...span.span.tokenIndexList
							);
							situationMentionMap.set(
								span.span.spanId.uuidString,
								updateMention
							);
						}
					} else {
						if (span.mentionModel === "entity") {
							const mention: EntityMention = {
								entityType: span.span.type || "",
								tokens: {
									tokenIndexList: span.span.tokenIndexList,
									tokenizationId: span.span.tokenizationId,
								},
								uuid: span.span.spanId,
							};
							entityMentionMap.set(
								span.span.spanId.uuidString,
								mention
							);
						} else {
							const situationMention: SituationMention = {
								situationType: span.span.type || "",
								uuid: span.span.spanId,
								tokens: {
									tokenIndexList: span.span.tokenIndexList,
									tokenizationId: span.span.tokenizationId,
								},
								argumentList: [],
							};
							situationMentionMap.set(
								span.span.spanId.uuidString,
								situationMention
							);
						}
					}
				});
			});

			entityMentionMap.forEach((mention, uuid) => {
				entityMentionList.push(mention);
			});

			situationMentionMap.forEach((situation, uuid) => {
				situationMentionList.push(situation);
			});

			if (output.entityMentionSetList === undefined) {
				output.entityMentionSetList = [];
			}
			if (output.situationMentionSetList === undefined) {
				output.situationMentionSetList = [];
			}

			//TODO: Theory dependency
			const entityMentionSet: EntityMentionSet = {
				uuid: {
					uuidString: uuidv4(),
				},
				metadata: metadata,
				mentionList: entityMentionList,
			};

			const situationMentionSet: SituationMentionSet = {
				uuid: {
					uuidString: uuidv4(),
				},
				metadata: metadata,
				mentionList: situationMentionList,
			};

			output.entityMentionSetList.push(entityMentionSet);
			output.situationMentionSetList.push(situationMentionSet);

			const entitySet: EntitySet = {
				uuid: {
					uuidString: uuidv4(),
				},
				metadata: metadata,
				entityList: entityList,
			};

			const entityUUIDSet: Set<string> = new Set(
				entityList.map((entity) => entity.uuid.uuidString)
			);

			const updatedSituationList: Situation[] = [];

			situationList.forEach((situation) => {
				const argList: Argument[] = [];

				situation.argumentList?.forEach((arg) => {
					arg.entityId?.forEach((entityId) => {
						if (entityUUIDSet.has(entityId)) {
							argList.push({
								role: arg.role,
								entityId: {
									uuidString: entityId,
								},
							});
						}
					});

					arg.situationId?.forEach((situationId) => {
						argList.push({
							role: arg.role,
							situationId: {
								uuidString: situationId,
							},
						});
					});
				});

				updatedSituationList.push({
					...situation,
					argumentList: argList,
				});
			});

			const situationSet: SituationSet = {
				uuid: {
					uuidString: uuidv4(),
				},
				metadata: metadata,
				situationList: updatedSituationList,
			};

			if (entityList.length) {
				if (output.entitySetList === undefined) {
					output.entitySetList = [];
				}
				output.entitySetList.push(entitySet);
			}

			if (updatedSituationList.length) {
				if (output.situationSetList === undefined) {
					output.situationSetList = [];
				}

				output.situationSetList.push(situationSet);
			}
		} else if (props.annotationScheme.kind === "mention") {
			const metadata = {
				tool: "FACTSLab",
				timestamp: Math.trunc(Date.now() / 1000), // TODO
				theoryDependencies: theoryDependencies.current,
			};

			if (props.annotationScheme.subkind === "entity") {
				const mentionList = returnEntityMentionList(
					spanToTypeMap,
					spanTreeMap
				);
				const entityMentionSet: EntityMentionSet = {
					uuid: {
						uuidString: uuidv4(),
					},
					metadata: metadata,
					mentionList: mentionList,
				};

				if (output.entityMentionSetList === undefined) {
					output.entityMentionSetList = [];
				}
				output.entityMentionSetList.push(entityMentionSet);
			} else if (props.annotationScheme.subkind === "situation") {
				const situationMentionList: SituationMention[] =
					returnSituationMentionList(
						spanToTypeMap,
						spanTreeMap,
						situationArgumentMap
					);
				const situationMentionSet: SituationMentionSet = {
					uuid: {
						uuidString: uuidv4(),
					},
					metadata: metadata,
					mentionList: situationMentionList,
				};

				if (output.situationMentionSetList === undefined) {
					output.situationMentionSetList = [];
				}

				output.situationMentionSetList.push(situationMentionSet);
			}
		}

		// exportData(output);
		return output;
	}

	const exportData = () => {
		const data = saveToConcrete();
		const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
			JSON.stringify(data)
		)}`;
		const link = document.createElement("a");
		link.href = jsonString;
		link.download = "annotated-data.json";
		link.click();
	};

	const handleMturkSubmit = () => {	
		const outputCommuncation = saveToConcrete();
		const communicationInput = document.querySelector("#communication_input") as HTMLInputElement;
		if (communicationInput) {
			communicationInput.value = JSON.stringify(outputCommuncation);
		}
		const annotationSchemeInput = document.querySelector("#annotation_scheme_input") as HTMLInputElement;
		if (annotationSchemeInput) {
			annotationSchemeInput.value = JSON.stringify(props.annotationScheme);
		}
		const userInteractionInput = document.querySelector("#user_interaction_input") as HTMLInputElement;
		if (userInteractionInput) {
			userInteractionInput.value = JSON.stringify(userInteractions);
		}
	}

	const TokenizationContextValue = {
		highlightedMentions,
		activateStructureIds,
		activeTypeIds,
		entityMentionSetList: props.entityMentionSetList || [],
		situationMentionSetList: props.situationMentionSetList || [],
		addToSpanMap,
		deleteFromSpanMap,
		setDeleteSpanCallback,
		metadata,
		activeSituation,
		activateSituationId,
		deactivateStructureIds,
		spanToTypeMap,
		setSpanToTypeMap,
		setSituationArgumentMap,
		metaKeyClicked,
		shiftKeyClicked,
		addToUserInteractions,
		spanMap,
		hoveredSpan,
		setHoveredSpan,
	};

	/** Render the menu (if the annotation scheme requires one) */
	function renderMenu() {
		//Switch between situation and mention annotation schemes
		if (props.annotationScheme.kind === "referent") {
			return (
				<ReferentAnnotation
					activeStructureIds={activeStructureIds}
					activateStructureIds={activateStructureIds}
					deactivateStructureIds={deactivateStructureIds}
					activeTypeIds={activeTypeIds}
					annotationScheme={props.annotationScheme}
					entityList={entityList}
					situationList={situationList}
					spanMap={spanMap}
					activateTypeIds={activateTypeIds}
					addMentionToEntity={addMentionToEntity}
					createEntity={createEntity}
					deleteEntity={deleteEntity}
					mutateEntity={mutateEntity}
					removeMentionFromEntity={removeMentionFromEntity}
					addMentionToSituation={addMentionToSituation}
					createSituation={createSituation}
					deleteSituation={deleteSituation}
					mutateSituation={mutateSituation}
					removeMentionFromSituation={removeMentionFromSituation}
					setMentionModel={setMentionModel}
					addToUserInteractions={addToUserInteractions}
					displayToast={displayToast}
				/>
			);
		} else if (props.annotationScheme.kind === "mention") {
			const mentionList =
				props.annotationScheme.subkind === "entity"
					? entityMentionList.current
					: situationMentionList.current;
			return (
				<MentionAnnotationContext.Provider
					value={MentionAnnotationContextValue}>
					<MentionAnnotation
						activeStructureIds={activeStructureIds}
						activeSituation={activeSituation}
						annotationScheme={props.annotationScheme}
						roleOntologyType={
							props.annotationScheme.subkind === "situation"
								? props.annotationScheme.roleOntologyType
								: "general"
						}
						mentionList={mentionList}
						activateTypeIds={activateTypeIds}
						activeTypeIds={activeTypeIds}
						spanMap={spanMap}
						setHighlightedMentions={setHighlightedMentions}
						activateSituation={activateSituationId}
						deactivateSituation={deactivateSituation}
						activateStructureIds={activateStructureIds}
						deactivateStructureIds={deactivateStructureIds}
					/>
				</MentionAnnotationContext.Provider>
			);
		} else {
			return null;
		}
	}

	/** Render the content of the communication */
	function renderContent() {
		const smaller = ["section", "sentence", "tokenspan", "token"];
		// MentionAnnotation/SituationAnnotation Switch
		const annotationScheme = props.annotationScheme;

		const useStructure =
			annotationScheme !== undefined
				? smaller.includes(annotationScheme.unit)
				: false;

		if (
			useStructure &&
			props.sectionList &&
			annotationScheme !== undefined
		) {
			const sections = props.sectionList.map((p) => (
				<Section
					key={p.uuid.uuidString}
					annotationScheme={
						annotationScheme as SectionAnnotationScheme
					}
					activeStructureIds={activeStructureIds}
					situationMentionSetList={props.situationMentionSetList}
					situationSetList={props.situationSetList}
					{...p}
				/>
			));
			let cName = "content ";
			if (metaKeyClicked) {
				if (shiftKeyClicked && activeStructureIds.length) {
					cName += "join";
				} else if (!shiftKeyClicked) {
					cName += "brush";
				}
			}
			return <div className={cName}>{sections}</div>;
		} else if (!useStructure) {
			return <span>{props.text}</span>;
		} else {
			alert("No sectionList for " + props.uuid.uuidString);
			return null;
		}
	}

	const logo = <div className="logo">{logoSVG}</div>;

	const header =
		props.annotationScheme.kind === "mention"
			? "Mention Annotation"
			: "Coreference Annotation";
	const __instructionContentSet = (modelType: any, schemeType: any) => {
		let htmlString = ""; 
		const imageStyle = { width: '100%' };
		if (modelType === "entity" && schemeType === "mention") {
			return (<>{basic_actions_instructions} {entity_mention_instructions}</>);
		} 
		if (modelType === "entity" && schemeType === "referent") {
			return (<>{basic_actions_instructions}{entity_refernt_instructions}</>);
		} 
		if (modelType === "situation" && schemeType === "mention") {
			return (<>{basic_actions_instructions}{situation_mention_instructions}</>);
		} 
		if (modelType === "situation" && schemeType === "referent") {
			return (<>{basic_actions_instructions}{situation_refernt_instructions}</>);
		} 
		return (
			<ErrorBoundary fallback={<div>There is an error parsing instructions for this annotation.</div>}>
					<div className={"empty-info"} dangerouslySetInnerHTML={{__html:htmlString || ""}}/>
			</ErrorBoundary>
		  );
		
	}
	const infoModal = (
		<Modal
			content={[instructionContent]}
			open={infoModalShow}
			handleClose={() => {
				setInfoModalShow(false);
			}}
			emptyInfo={
				props.annotationScheme.instructions === undefined
					? "No instructions provided"
					: undefined
			}
			header={"Task Information"}
			mentionModel={mentionModel}
		/>
	);

	const toolTipItems: ToolTipItemProps[] = [
		{
			keys: ["Command", "Click"],
			description: "Creates a span",
		},
		{
			keys: ["Command", "Shift", "Click"],
			description: "Extends a currently active span",
		},
		{
			keys: ["Delete"],
			description: "Deletes the currently active span",
		},
	];

	if (
		props.annotationScheme.kind === "referent" &&
		props.annotationScheme.subkind === "situation"
	) {
		toolTipItems.push({
			keys: ["Command", "/"],
			description: "Switch modes between Entity/Situation",
		});
	} else if (
		props.annotationScheme.kind === "mention" &&
		props.annotationScheme.subkind === "situation"
	) {
		toolTipItems.push({
			keys: ["Command", "/"],
			description: "Switch modes between situation-typing/role-typing",
		});
	}

	const submitButton = document.querySelector("#hiddenSubmit") === null
						? <div
							className={
								mentionModel === "entity"
									? "save-button-entity"
									: "save-button-situation"
							}
							onClick={exportData}>
							Save
						</div>
						: <input 
							className={
								mentionModel === "entity"
									? "save-button-entity submit"
									: "save-button-situation submit"
							} 
							onClick={handleMturkSubmit}
							type="submit" 
							value="Submit" />;

	/** Render the communication */
	return (
		<ModalOpenContext.Provider value={{ modalOpen, setModalOpen }}>
			<TokenizationContext.Provider value={TokenizationContextValue}>
				<MentionModelContext.Provider value={mentionModel}>
					<div className="new-card">
						<div className="r1">
							<div className="communication">
								<div className={"header " + mentionModel}>
									<div className="logo-name-div">
										{logo}
										<h1>{header}</h1>
									</div>
									<div
										className="info-tooltip"
										onClick={(e: React.MouseEvent) => {
											e.stopPropagation();
										}}>
										<p
											className={"info " + mentionModel}
											onClick={() => {
												setInfoModalShow(true);
											}}>
											i
										</p>
										<ToolTip
											items={toolTipItems}
											mentionModel={mentionModel}
										/>
									</div>
								</div>
								{renderContent()}
								{infoModal}
							</div>
							{renderMenu()}
						</div>
						<div>
							<Snackbar
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "center",
								}}
								open={toast}
								autoHideDuration={2000}
								onClose={() => {
									setToast(false);
								}}
								message={toastMessage}>
								<Alert severity={toastType}>
									{toastMessage}
								</Alert>
							</Snackbar>
						</div>
						<br></br>
						{submitButton}
					</div>
				</MentionModelContext.Provider>
			</TokenizationContext.Provider>
		</ModalOpenContext.Provider>
	);
}
